// ProfilePanel — My Account -> "Profile & security" section (Unit 1 "My
// Account" IA restructure, Task 4; tab layout per the account-area redesign
// Task 8 amendment, 2026-10-02 F17/F21).
//
// ⚠️ AMENDED 2026-10-04 (account-area redesign Task 8, F17/F21). ORIGINAL:
// three stacked sub-sections — the gated "Profile details" form, the gated
// "Addresses" list, the ungated "Security" block — with the addresses form
// rendered INLINE (replacing the list) and Security always mounted below.
// CURRENT: the section's kinds of information are TABS from the registry
// (SECTION_TABS.profile, rendered with EmbedTabs like ProductsTab/ReferPanel):
//   - "Profile" (ungated): the gated contact form PLUS the read-only
//     identity rows — the rows moved OUT of the form's card into their own
//     ungated section ("Account details") because the amendment makes them
//     ALWAYS visible, and a form card cannot render for a member denied
//     manage-account-settings; and the Leave slot (see leaveSlot below) as
//     the pane's LAST element child (ruling R2: a member whose role lacks
//     manage-membership must still reach it — Members stays gated);
//   - "Addresses" (gated on ACL_AREA_MANAGE_ADDRESSES — the TAB ITSELF
//     hides when denied): the shell-owned list rendered as an auto-fill
//     minmax(220px, 1fr) grid of bordered cards (plan Step 3: borderSubtle,
//     8px radius, no shadow), plus CRUD inside an
//     EmbedDialog, opened by one "Add address" button; the Shipping/Billing
//     choice is an EmbedSegmented shown in ADD mode only (edit PATCHes carry
//     no address_type — the form ignores `variant` when editing anyway);
//   - "Sign-in & security" (ungated): SecurityPanel, mounted ONLY while its
//     tab is active (it was previously always mounted — its list fetch now
//     waits for the tab, which AccountCenter.test.tsx re-pins).
// No Step-1 pin loosened: the form's max width, its submit copy, the
// read-only email note, and AddressForm's own chrome are unchanged (the
// email note text itself is re-pinned by Step 1 — see the rows below).
//
// THE INVARIANT THIS FILE EXISTS TO ENFORCE (design.md, Unit-1 plan Task 4,
// carried through the Task 8 amendment): dropping the section-level gates
// means the two ACL areas reappear HERE so a member denied one area doesn't
// gain the other, and a member denied BOTH still lands on the Profile tab
// (read-only rows + Leave). The tab filter is NOT visibleTabs()
// (account-sections): that helper fails open on an unloaded self-view, and
// the amendment requires the settle conjunct so a denied member's browser
// never paints the Addresses tab before the ACL answer arrives. Uses
// `isAreaPermitted(selfView, area?)` from ./account-sections (Task 3) —
// no second gating predicate.
//
// STATE OWNERSHIP (design.md §6, resolved by the task-4 brief): AccountCenter
// keeps the address LIST and its fetch — shared data the Overview landing
// counts from; a second fetch here would create a second source of truth.
// This panel owns only the INTERACTION state nobody else reads (addressMode,
// addAddressVariant, editingAddressTarget, pendingDeleteId, deletingId,
// settingDefaultId, plus account/profileError for the details form).
// Mutations call onAddressesChanged(next) so the shell's single copy stays
// authoritative.
//
// Inline styles only -- no Tailwind, no portal/ imports (embed convention).
import { useState, useEffect, useCallback, type FormEvent, type Dispatch, type ReactNode, type SetStateAction } from "react";
import {
  useEmbedTokens,
  EmbedCard,
  EmbedText,
  EmbedButton,
  EmbedLink,
  EmbedBanner,
  EmbedField,
  EmbedInput,
  EmbedTabs,
  EmbedSegmented,
  EmbedDialog,
  EmbedStatusBadge,
} from "../../embed-shared/primitives";
import { useContainerWidth } from "../../embed-shared/use-container-width";
import { embedApiFetch } from "../../embed-shared/api-client";
import type { CustomerAccount } from "../../embed-shared/types";
import { humanizeEnum } from "../../embed-shared/status";
import { updateAddress, deleteAddress } from "./address-api";
import type { AddressWire } from "./address-api";
import { AddressForm } from "./AddressForm";
import { SecurityPanel } from "./SecurityPanel";
import { isAreaPermitted, SECTION_TABS } from "./account-sections";
// ACL_AREA_MANAGE_ADDRESSES is deliberately NOT imported here: the Addresses
// tab gates through SECTION_TABS.profile's `area` field (§55 — the constant
// lives in the registry, consumers read the field), and only the contact
// form's own gate below names its constant directly.
import { ACL_AREA_MANAGE_ACCOUNT_SETTINGS, type SelfViewWire } from "./membership-api";

// SECTION_TABS is a Partial record (every section COULD be tab-less); the
// profile entry is static, so the ?? [] fallback only satisfies the type.
const PROFILE_TABS = SECTION_TABS.profile ?? [];

/**
 * Container width at or above which the contact form's "Contact name" and
 * "Phone" fields share one two-column row instead of stacking. Measured on
 * the "Profile details" section itself (max-width formMax), not the
 * viewport: the embed lives inside a host-page column whose width the
 * window says nothing about. Below it the fields stack.
 */
export const PROFILE_CONTACT_ROW_MIN_PX = 520;

/**
 * The address cards' grid track template (plan Task 8 Step 3, review I-1 fix
 * round): auto-fill of 220px-minimum tracks, so the cards flow edge-to-edge
 * at any host-page width — one column below ~460px of container, more above
 * — without a breakpoint. Measured against the pane, not the viewport, for
 * the same reason PROFILE_CONTACT_ROW_MIN_PX is.
 */
export const ADDRESS_CARD_GRID_COLUMNS = "repeat(auto-fill, minmax(220px, 1fr))";

export interface ProfilePanelProps {
  selfView: SelfViewWire | null;
  /** I-3 (final whole-branch review): the same shape of mount-timing fix
   *  the deleted WelcomePanel carried (its header, Task 5 review round 1 —
   *  ⚠️ AMENDED 2026-10-03, redesign Task 6: that panel is deleted; the
   *  idiom's surviving carriers are this gate and OverviewPanel's settle
   *  discipline), NOT full parity with it (residual 5b, final whole-branch
   *  re-review) — gates the ACL-governed TABS' RENDER alongside
   *  isAreaPermitted so a denied member's own data (e.g. an address) never
   *  flashes on screen before the first self-view attempt has resolved.
   *  `isAreaPermitted(null, area)` is fail-open by design (unloaded
   *  self-view hides nothing — the server's own refusal remains the
   *  control), so without this an unsettled read renders the gated tab for
   *  one round trip regardless of outcome. Set in AccountCenter's
   *  `loadSelfView` `finally` block — true on BOTH success and failure, so
   *  fail-open is preserved, never inverted. UNLIKE the deleted panel, this
   *  gate does NOT suppress the underlying fetch: `loadAccount()` below is
   *  unconditional — it still issues `GET /me/account` immediately on mount
   *  regardless of this flag, because the ungated read-only rows need the
   *  same account object the gated form edits. The visual flash is fixed;
   *  the early, potentially-denied request is not. */
  selfViewSettled: boolean;
  addresses: AddressWire[];
  addressesError: string | null;
  /**
   * Widened beyond the brief's literal `(next: AddressWire[]) => void` —
   * disclosed deviation, review finding I-1 (task-4-report.md). The three
   * address mutation handlers below pass FUNCTIONAL updaters (`prev => ...`)
   * rather than computing the next array from the closed-over `addresses`
   * prop, because the prop-capturing form is a stale-closure lost-update:
   * two mutations kicked off before either resolves (e.g. Remove on one
   * card racing Set-default on another — nothing here disables sibling
   * cards mid-flight) both close over the SAME pre-mutation snapshot, so
   * whichever resolves second overwrites the first's result from that
   * stale snapshot, resurrecting what the first just removed. This type is
   * exactly React's own `Dispatch<SetStateAction<...>>` — AccountCenter
   * already passes `setAddresses` here verbatim, so this only widens the
   * prop to admit the functional-updater form it was already receiving.
   */
  onAddressesChanged: Dispatch<SetStateAction<AddressWire[]>>;
  passkeyEligible?: boolean;
  /**
   * Additive beyond the task-4 brief's literal prop list, disclosed in
   * task-4-report.md: AccountCenter keeps owning the address LIST FETCH
   * (shared data), including its in-flight state, so the moved Addresses
   * tab needs this to still show "Loading addresses..." instead of
   * flashing the empty state during a slow initial load — the ONE piece of
   * original behavior the literal prop list couldn't carry unchanged.
   * Optional + defaulted so it doesn't change this panel's test contract.
   */
  loadingAddresses?: boolean;
  /**
   * Account-area redesign Task 8 (F17/F21, ruling R2): the Leave affordance
   * travels from the shell as a SLOT so AccountCenter keeps owning the
   * shouldShowLeave condition and the block's callbacks, while THIS panel
   * owns the placement — rendered only while the Profile tab is active and
   * always as that pane's LAST element child. Profile is ungated, so every
   * member can reach Leave even when their role lacks manage-membership;
   * an owner's slot is null and renders nothing. Keeping this a slot (not
   * a `shouldShowLeave` + props re-plumbing) means the shell's existing
   * leave-done banner/focus wiring moves nowhere.
   */
  leaveSlot?: ReactNode;
  /**
   * M-8 (final fix wave): a cross-section deep link's REGISTRY tab id (the
   * shell's consume-once pendingTab — Overview's "Manage addresses" targets
   * "addresses"). Seeds the initial selection only; the clamp below still
   * falls back to the first visible tab for an unknown or ACL-hidden id, and
   * later renders cannot re-point the member's own tab choice (ReferPanel's
   * initialSubTab idiom).
   */
  initialSubTab?: string;
}

export function ProfilePanel({
  selfView,
  selfViewSettled,
  addresses,
  addressesError,
  onAddressesChanged,
  passkeyEligible,
  loadingAddresses = false,
  leaveSlot,
  initialSubTab,
}: ProfilePanelProps) {
  const t = useEmbedTokens();

  // ---- Tabs (amendment F17/F21). Selection is React state only (rule 7:
  // never storage). requestedTab is CLAMPED against the currently-visible
  // list: a self-view reload that hides the tab the member is on (the
  // post-transfer demotion) falls back to the first visible tab instead of
  // leaving EmbedTabs' value pointing at a tab that no longer renders.
  // (ProductsTab idiom — useState on the registry's first id — plus the
  // clamp its static tab list never needed.) ----
  const [requestedTab, setRequestedTab] = useState<string | null>(initialSubTab ?? null);
  const visibleTabs = PROFILE_TABS.filter(
    (tab) => tab.area == null || (selfViewSettled && isAreaPermitted(selfView, tab.area)),
  );
  const activeTabId = visibleTabs.some((tab) => tab.id === requestedTab)
    ? (requestedTab as string)
    : (visibleTabs[0]?.id ?? "profile");

  // ---- Profile: lazily fetched once on mount (mirrors the prior
  // AccountCenter behavior of loading the first time the Profile section
  // opened — this panel is only ever mounted while that section is active,
  // so "on mount" and "on first activation" are the same moment now). The
  // fetch is UNCONDITIONAL: the ungated read-only rows (the "Account
  // details" section below) read the same object the gated form edits. ----
  const [account, setAccount] = useState<CustomerAccount | null>(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [loadingAccount, setLoadingAccount] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [phone, setPhone] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState(false);

  const loadAccount = useCallback(async () => {
    setLoadingAccount(true);
    setProfileError(null);
    try {
      const data = await embedApiFetch<CustomerAccount>("/api/v1/marketplace/me/account");
      setAccount(data);
      setCompanyName(data?.company_name ?? "");
      setDisplayName(data?.display_name ?? "");
      setPhone(data?.phone ?? "");
    } catch (err) {
      // err.message for ANY Error (ApiError included — it extends Error):
      // the spec rejects with a PLAIN Error ("network down") and pins that
      // exact text in the banner, so an ApiError-only instanceof mapping
      // would show a generic fallback instead.
      setProfileError(err instanceof Error ? err.message : "Failed to load profile");
    } finally {
      setLoadingAccount(false);
    }
  }, []);

  useEffect(() => {
    if (!profileLoaded) {
      setProfileLoaded(true);
      loadAccount();
    }
  }, [profileLoaded, loadAccount]);

  const handleSaveProfile = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!account) return;
      setSavingProfile(true);
      setProfileError(null);
      setProfileSuccess(false);
      try {
        // email is DISPLAY-ONLY (server enforces via 400 EMAIL_READ_ONLY) —
        // never included here. All three fields follow the
        // empty-input-clears-to-null convention (the server clears on an
        // explicit null, TD-683).
        // TD-690: every field is sent ONLY when it changed. Without the
        // company_name key the server treats the save as contact-only: it
        // writes just this member's own row and leaves the shared account
        // version alone, so a member updating their phone no longer 409s
        // another member's company edit. If-Match is still sent; the server
        // ignores it then — which is why display_name/phone are diffed too:
        // a contact-only write has no lock on this form's snapshot, so an
        // unchanged field written back would silently revert a newer edit.
        const body: Record<string, string | null> = {};
        const nextDisplayName = displayName.trim() || null;
        if (nextDisplayName !== (account.display_name?.trim() || null)) {
          body.display_name = nextDisplayName;
        }
        const nextPhone = phone.trim() || null;
        if (nextPhone !== (account.phone?.trim() || null)) {
          body.phone = nextPhone;
        }
        const nextCompany = companyName.trim() || null;
        if (nextCompany !== (account.company_name?.trim() || null)) {
          body.company_name = nextCompany;
        }
        const updated = await embedApiFetch<CustomerAccount>("/api/v1/marketplace/me/account", {
          method: "PATCH",
          body,
          headers: { "If-Match": String(account.version) },
        });
        // §59: re-seed local state from the MUTATION RESPONSE, which carries
        // the incremented version — an onSuccess that only re-fetches (or
        // does nothing) strands the stale version and the customer's own
        // NEXT consecutive save 409s on their own prior successful save.
        // Extends to every editable field the response carries, not just
        // company_name/version — a server-side normalization on
        // display_name/phone (or a stale value from a slower re-fetch) must
        // never overwrite what the response actually returned.
        setAccount(updated);
        setCompanyName(updated?.company_name ?? "");
        setDisplayName(updated?.display_name ?? "");
        setPhone(updated?.phone ?? "");
        setProfileSuccess(true);
      } catch (err) {
        // err.message for any Error — same mapping as loadAccount above.
        setProfileError(err instanceof Error ? err.message : "Failed to save profile");
      } finally {
        setSavingProfile(false);
      }
    },
    [account, companyName, displayName, phone],
  );

  // Contact name + Phone share one row when the form column is wide enough
  // (or unmeasured — useContainerWidth fails OPEN, and a broken observer
  // must show the readable wide layout, not a cramped one).
  const [formRef, formWidth] = useContainerWidth();
  const shareContactRow = formWidth === null || formWidth >= PROFILE_CONTACT_ROW_MIN_PX;
  const contactNameField = (
    <EmbedField label="Contact name" htmlFor="profile-display-name">
      <EmbedInput
        id="profile-display-name"
        type="text"
        value={displayName}
        onChange={(e) => {
          setDisplayName(e.target.value);
          setProfileSuccess(false);
        }}
      />
    </EmbedField>
  );
  const phoneField = (
    <EmbedField label="Phone" htmlFor="profile-phone">
      <EmbedInput
        id="profile-phone"
        type="tel"
        value={phone}
        onChange={(e) => {
          setPhone(e.target.value);
          setProfileSuccess(false);
        }}
      />
    </EmbedField>
  );

  // ---- Addresses: full CRUD (AC3). AccountCenter owns the LIST + fetch
  // (addresses/addressesError/loadingAddresses props); this panel owns only
  // the interaction state below. Since the Task 8 amendment the FORM lives
  // inside an EmbedDialog on top of the list (nothing here replaces the
  // list view anymore); addressMode still names which dialog is open. ----
  type AddressMode = "list" | "add" | "edit";
  const [addressMode, setAddressMode] = useState<AddressMode>("list");
  const [addAddressVariant, setAddAddressVariant] = useState<"shipping" | "billing">("shipping");
  const [editingAddressTarget, setEditingAddressTarget] = useState<AddressWire | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [settingDefaultId, setSettingDefaultId] = useState<number | null>(null);
  // Mutation-only error (delete/set-default) — distinct from the fetch error
  // AccountCenter owns (addressesError prop) since this panel has no setter
  // for that prop. Merged for display at the top of the pane, EXCEPT while
  // the remove dialog is open — a delete failure renders in the dialog
  // (next to its action), and the slot moves to the list banner only after
  // dismissal, so the same text never renders twice at once.
  const [addressActionError, setAddressActionError] = useState<string | null>(null);
  const addressesErrorToShow =
    pendingDeleteId != null ? addressesError : (addressesError ?? addressActionError);

  // The list this panel RENDERS. AccountCenter owns the address LIST and its
  // fetch (shared with the Overview landing) — but the panel must reflect its
  // own mutations immediately rather than waiting on the parent's state
  // round-trip, and the unit spec pins exactly that by driving this panel
  // with a NO-OP onAddressesChanged (ProfilePanel.test.tsx baseProps) while
  // still expecting the created card / removed card / moved Default badge to
  // render. So every mutation applies the SAME functional updater to this
  // mirror AND forwards it to onAddressesChanged; whenever the authoritative
  // prop then changes — the shell's fetch landing, or its own state settling
  // after a forwarded update — this effect re-syncs the mirror to it. The
  // updater is pure, so the double application is idempotent; functional form
  // on BOTH sides keeps the I-1 out-of-order race safe (each side composes
  // over the latest list it holds, never over a captured snapshot).
  const [renderedAddresses, setRenderedAddresses] = useState<AddressWire[]>(addresses);
  useEffect(() => {
    setRenderedAddresses(addresses);
  }, [addresses]);

  // Add mode always starts on Shipping (the segment's documented default) —
  // reopening the dialog after a Billing add must not pre-select Billing.
  const handleStartAdd = useCallback(() => {
    setAddAddressVariant("shipping");
    setAddressMode("add");
  }, []);

  const handleStartEdit = useCallback((addr: AddressWire) => {
    setEditingAddressTarget(addr);
    setAddressMode("edit");
  }, []);

  const handleCancelAddressForm = useCallback(() => {
    setAddressMode("list");
    setEditingAddressTarget(null);
  }, []);

  // Fires for BOTH create and edit — AddressForm calls this one callback in
  // either mode (see AddressForm.tsx). Upsert by id: new id → append,
  // existing id → replace in place. Closing the mode is what closes the
  // dialog (the dialogs' `open` props key off addressMode).
  //
  // I-1 fix: functional updater, not a computation over the closed-over
  // `addresses` prop — see ProfilePanelProps.onAddressesChanged's doc
  // comment for why the prop-capturing form was a lost-update race. The SAME
  // updater is applied to the rendered mirror (see its declaration) so the
  // card lands without waiting on the parent.
  const handleAddressSaved = useCallback(
    (id: number, addr: AddressWire) => {
      const upsert = (prev: AddressWire[]) => {
        const exists = prev.some((a) => a.id === id);
        return exists ? prev.map((a) => (a.id === id ? addr : a)) : [...prev, addr];
      };
      setRenderedAddresses(upsert);
      onAddressesChanged(upsert);
      setAddressMode("list");
      setEditingAddressTarget(null);
    },
    [onAddressesChanged],
  );

  const handleRequestDelete = useCallback((id: number) => {
    // Clear any prior action error FIRST (StoresPanel's onRemove idiom):
    // while the dialog is open the pane-top slot suppresses
    // addressActionError, so a stale value from a PREVIOUS failed delete
    // would otherwise render inside this fresh dialog, misattributed to an
    // action not yet attempted.
    setAddressActionError(null);
    setPendingDeleteId(id);
  }, []);

  const handleCancelDelete = useCallback(() => {
    setPendingDeleteId(null);
  }, []);

  // I-1 fix: functional updater — see handleAddressSaved's comment above.
  const handleConfirmDelete = useCallback(
    async (addr: AddressWire) => {
      setDeletingId(addr.id);
      setAddressActionError(null);
      try {
        await deleteAddress(addr.id, addr.version);
        const remove = (prev: AddressWire[]) => prev.filter((a) => a.id !== addr.id);
        setRenderedAddresses(remove);
        onAddressesChanged(remove);
        setPendingDeleteId(null);
      } catch (err) {
        // err.message, not an ApiError-only mapping: the spec drives these
        // flows with PLAIN Errors (network down / version mismatch) and pins
        // their text on screen. ApiError extends Error, so its message still
        // wins when it is one.
        setAddressActionError(err instanceof Error ? err.message : "Failed to remove address");
      } finally {
        setDeletingId(null);
      }
    },
    [onAddressesChanged],
  );

  // I-1 fix: functional updater — see handleAddressSaved's comment above.
  const handleSetDefault = useCallback(
    async (addr: AddressWire) => {
      setSettingDefaultId(addr.id);
      setAddressActionError(null);
      try {
        const updated = await updateAddress(addr.id, { version: addr.version, is_default: true });
        const promote = (prev: AddressWire[]) =>
          prev.map((a) => {
            if (a.id === updated.id) return updated;
            // Optimistic same-session demotion ONLY — the customer-facing
            // PATCH does not run the admin surface's UnsetDefaultsForTypeInTx
            // invariant (verified via grep, see file-header comment in
            // address-api.ts), so this does not change server-side truth. A
            // page reload could still show two defaults of the same type
            // until that backend gap is fixed.
            if (a.address_type === updated.address_type && a.is_default) {
              return { ...a, is_default: false };
            }
            return a;
          });
        setRenderedAddresses(promote);
        onAddressesChanged(promote);
      } catch (err) {
        // err.message — same plain-Error mapping as handleConfirmDelete.
        setAddressActionError(err instanceof Error ? err.message : "Failed to set default address");
      } finally {
        setSettingDefaultId(null);
      }
    },
    [onAddressesChanged],
  );

  const pendingDelete =
    pendingDeleteId != null
      ? (renderedAddresses.find((a) => a.id === pendingDeleteId) ?? null)
      : null;

  return (
    <div data-testid="profile-tab-pane" style={{ display: "flex", flexDirection: "column", gap: t.spacing.lg }}>
      <EmbedTabs
        aria-label="Profile sections"
        items={visibleTabs.map((tab) => ({ id: tab.id, label: tab.label }))}
        value={activeTabId}
        onChange={setRequestedTab}
      />

      {activeTabId === "profile" && (
        <>
          {selfViewSettled && isAreaPermitted(selfView, ACL_AREA_MANAGE_ACCOUNT_SETTINGS) && (
            <section aria-label="Profile details" ref={formRef} style={{ maxWidth: t.size.formMax }}>
              {loadingAccount && (
                <p role="status" style={{ margin: 0 }}>
                  <EmbedText tone="secondary">Loading profile...</EmbedText>
                </p>
              )}
              {!loadingAccount && account && (
                <EmbedCard>
                  <form onSubmit={handleSaveProfile} noValidate>
                    {profileError && (
                      <div style={{ marginBottom: t.spacing.md }}>
                        <EmbedBanner tone="error">{profileError}</EmbedBanner>
                      </div>
                    )}
                    {profileSuccess && !profileError && (
                      <div style={{ marginBottom: t.spacing.md }}>
                        <EmbedBanner tone="success">Profile updated.</EmbedBanner>
                      </div>
                    )}

                    <EmbedField label="Company name" htmlFor="profile-company-name">
                      <EmbedInput
                        id="profile-company-name"
                        type="text"
                        value={companyName}
                        onChange={(e) => {
                          setCompanyName(e.target.value);
                          setProfileSuccess(false);
                        }}
                      />
                    </EmbedField>

                    {/* Task 8 Step 3: the two half-width fields share one
                        row at/above PROFILE_CONTACT_ROW_MIN_PX (or when
                        unmeasured — fail open), stack below it. */}
                    {shareContactRow ? (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: t.spacing.md }}>
                        {contactNameField}
                        {phoneField}
                      </div>
                    ) : (
                      <>
                        {contactNameField}
                        {phoneField}
                      </>
                    )}

                    <div style={{ marginTop: t.spacing.md }}>
                      {/* Step-1 pin: sentence case on THIS form. (Task 12 sweep: the
                          address dialog's former camel-case "Save Changes"
                          is sentence case now too; both pins stay.) */}
                      <EmbedButton type="submit" variant="primary" disabled={savingProfile} loading={savingProfile}>
                        {savingProfile ? "Saving..." : "Save changes"}
                      </EmbedButton>
                    </div>
                  </form>
                </EmbedCard>
              )}
            </section>
          )}

          {/* The read-only identity rows are ALWAYS visible (amendment):
              they moved out of the form's card so a member denied
              manage-account-settings — who gets no form at all — still sees
              who they are signed in as, including the sign-in email this
              surface can never change. */}
          {account && (
            <section aria-label="Account details">
              <EmbedCard>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "140px 1fr",
                    rowGap: t.spacing.sm,
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontSize: t.fontSize.xs, color: t.colors.textSecondary }}>Email</span>
                  {/* TD-476: this is the "1fr" track of a 140px/1fr grid, and a grid
                      item defaults to min-width:auto exactly like a flex item — so the
                      track would not shrink below the email's min-content width and the
                      row ran 6px past its container at 400px. Same minWidth:0 +
                      `anywhere` pairing as MembersPanel, and see that file for why
                      `anywhere` is preferred over `break-word` (robustness if
                      minWidth:0 is ever dropped — not because break-word fails). */}
                  <div style={{ minWidth: 0 }}>
                    <span
                      id="profile-email-value"
                      aria-describedby="profile-email-note"
                      style={{
                        fontSize: t.fontSize.xs,
                        color: t.colors.textSecondary,
                        overflowWrap: "anywhere",
                      }}
                    >
                      {account.email}
                    </span>
                    <div
                      id="profile-email-note"
                      style={{ fontSize: t.fontSize.xs, color: t.colors.textSecondary, marginTop: 2 }}
                    >
                      You sign in with this email.
                    </div>
                  </div>
                  <span style={{ fontSize: t.fontSize.xs, color: t.colors.textSecondary }}>Account type</span>
                  <EmbedText tone="secondary" size="xs">
                    <span>{humanizeEnum(account.customer_type ?? "")}</span>
                  </EmbedText>
                  <span style={{ fontSize: t.fontSize.xs, color: t.colors.textSecondary }}>Tax exempt</span>
                  <EmbedText tone="secondary" size="xs">
                    {account.tax_exempt ? "Yes" : "No"}
                  </EmbedText>
                </div>
              </EmbedCard>
            </section>
          )}

          {/* loadAccount's failure surface is UNGATED, exactly like the rows
              it backs (review P2 fix, 2026-10-04): this card used to live
              only inside the gated form section above, so a member DENIED
              manage-account-settings whose GET /me/account failed got a
              blank Profile tab — no rows, no error, no retry — because
              loadAccount runs unconditionally for these rows. */}
          {!loadingAccount && !account && (
            <EmbedCard>
              {profileError ? (
                <>
                  <EmbedBanner tone="error">{profileError}</EmbedBanner>
                  <div style={{ marginTop: t.spacing.md }}>
                    <EmbedButton variant="secondary" onClick={loadAccount}>Retry</EmbedButton>
                  </div>
                </>
              ) : (
                <EmbedText tone="secondary">Profile is unavailable right now.</EmbedText>
              )}
            </EmbedCard>
          )}

          {/* Ruling R2 / F17/F21: the shell's Leave slot — the pane's LAST
              element child, Profile tab only (unmounts with the pane). */}
          {leaveSlot}
        </>
      )}

      {activeTabId === "addresses" && (
        <div data-testid="address-pane" style={{ display: "flex", flexDirection: "column", gap: t.spacing.md }}>
          {addressesErrorToShow && (
            <EmbedBanner tone="error">{addressesErrorToShow}</EmbedBanner>
          )}
          {loadingAddresses && (
            <p role="status" style={{ margin: 0 }}>
              <EmbedText tone="secondary">Loading addresses...</EmbedText>
            </p>
          )}
          {!loadingAddresses && renderedAddresses.length === 0 && (
            <div
              role="status"
              style={{ textAlign: "center", padding: t.spacing.xl }}
            >
              <div style={{ marginBottom: t.spacing.md }}>
                <EmbedText tone="secondary">No saved addresses.</EmbedText>
              </div>
              <EmbedButton variant="secondary" onClick={handleStartAdd}>
                Add address
              </EmbedButton>
            </div>
          )}
          {!loadingAddresses && renderedAddresses.length > 0 && (
            <>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <EmbedButton variant="secondary" onClick={handleStartAdd}>
                  Add address
                </EmbedButton>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: ADDRESS_CARD_GRID_COLUMNS, gap: t.spacing.sm }}>
                {renderedAddresses.map((addr) => (
                  <div key={addr.id} data-testid={`address-card-${addr.id}`}>
                    {/* Plan Step 3 card surface: borderSubtle border, 8px
                        radius, NO shadow. EmbedCard's own surface paints
                        cardBorder + cardShadow and takes no style overrides,
                        so the plan's surface is painted inline instead. */}
                    <div
                      style={{
                        background: t.colors.cardBg,
                        border: `1px solid ${t.colors.borderSubtle}`,
                        borderRadius: t.borderRadius,
                        padding: t.spacing.md,
                        boxSizing: "border-box",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: t.spacing.sm }}>
                        <span style={{ fontSize: t.fontSize.sm, fontWeight: 600, color: t.colors.text }}>
                          {humanizeEnum(addr.address_type ?? "")}
                        </span>
                        {addr.is_default && <EmbedStatusBadge tone="neutral" label="Default" />}
                      </div>
                      <div style={{ marginTop: t.spacing.xs }}>
                        <EmbedText tone="secondary">
                          {addr.line1}
                          {addr.line2 && `, ${addr.line2}`}
                        </EmbedText>
                      </div>
                      <div>
                        <EmbedText tone="secondary">
                          {addr.city}, {addr.state} {addr.postal_code}
                        </EmbedText>
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: t.spacing.md, marginTop: t.spacing.sm }}>
                        <EmbedLink onClick={() => handleStartEdit(addr)}>Edit</EmbedLink>
                        {!addr.is_default && (
                          <EmbedLink onClick={() => handleSetDefault(addr)}>
                            {settingDefaultId === addr.id ? "Setting..." : "Make default"}
                          </EmbedLink>
                        )}
                        <EmbedLink variant="danger" onClick={() => handleRequestDelete(addr.id)}>
                          Remove
                        </EmbedLink>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Add: one dialog; the Shipping/Billing choice is the segmented
              control (ADD MODE ONLY — an edit PATCH carries no
              address_type), and key={variant} remounts the form when the
              segment flips so the fields never carry values across the
              type switch. The form's own "Back" (its standalone-page
              affordance) closes the dialog, as do the dialog's own Escape
              and backdrop paths. */}
          <EmbedDialog
            open={addressMode === "add"}
            onClose={handleCancelAddressForm}
            title="Add address"
          >
            <div style={{ display: "flex", flexDirection: "column", gap: t.spacing.md }}>
              <EmbedSegmented
                aria-label="Address type"
                options={[
                  { id: "shipping", label: "Shipping" },
                  { id: "billing", label: "Billing" },
                ]}
                value={addAddressVariant}
                onChange={(id) => setAddAddressVariant(id === "billing" ? "billing" : "shipping")}
              />
              <AddressForm
                key={addAddressVariant}
                variant={addAddressVariant}
                onAddressCreated={handleAddressSaved}
                onBack={handleCancelAddressForm}
              />
            </div>
          </EmbedDialog>

          {/* Edit: the same form component in its edit mode (pre-filled,
              PATCHes with the row's id + version; the type choice is not
              offered). The form's "Cancel" closes the dialog. */}
          {editingAddressTarget && (
            <EmbedDialog
              open={addressMode === "edit"}
              onClose={handleCancelAddressForm}
              title="Edit address"
            >
              <AddressForm
                key={`edit-${editingAddressTarget.id}`}
                variant={editingAddressTarget.address_type === "billing" ? "billing" : "shipping"}
                editingAddress={editingAddressTarget}
                onAddressCreated={handleAddressSaved}
                onBack={handleCancelAddressForm}
              />
            </EmbedDialog>
          )}

          {/* Remove: the destructive confirm moved OUT of the card's inline
              links into an alertdialog (same pattern as LeaveAccountBlock /
              the store remove) — a remove can no longer be triggered by a
              double-activation chain inside the card row. A failure renders
              IN the dialog next to its action; the pane-top banner takes
              the slot back once the dialog is dismissed (see
              addressesErrorToShow above). */}
          <EmbedDialog
            role="alertdialog"
            open={pendingDelete !== null}
            onClose={handleCancelDelete}
            closeDisabled={deletingId !== null}
            title="Remove this address?"
            size="small"
            footer={
              <>
                <EmbedButton
                  variant="secondary"
                  onClick={handleCancelDelete}
                  disabled={deletingId !== null}
                >
                  Cancel
                </EmbedButton>
                <EmbedButton
                  variant="danger"
                  onClick={() => {
                    if (pendingDelete) handleConfirmDelete(pendingDelete);
                  }}
                  loading={deletingId !== null}
                >
                  Remove address
                </EmbedButton>
              </>
            }
          >
            <p style={{ margin: 0, fontSize: t.fontSize.sm, color: t.colors.text }}>
              This saved address will be removed from your account.
            </p>
            {pendingDelete !== null && addressActionError && (
              <div style={{ marginTop: t.spacing.md }}>
                <EmbedBanner tone="error">{addressActionError}</EmbedBanner>
              </div>
            )}
          </EmbedDialog>
        </div>
      )}

      {/* Sign-in & security: ungated (passkey management was never governed
          by the ACL matrix) and mounted ONLY while its tab is active — the
          panel's list fetch now waits for the tab instead of firing on
          section mount. */}
      {activeTabId === "security" && (
        <section aria-label="Security">
          <SecurityPanel passkeyEligible={passkeyEligible} />
        </section>
      )}
    </div>
  );
}
